import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Request failed');
      setSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-6">
      <div className="mx-auto w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/pulse-logo-with-name.png" alt="Pulse by ProjectAI" className="h-24 object-contain" />
          </Link>
        </div>

        <Card className="shadow-lg border-slate-200">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {sent ? 'Check your email' : 'Forgot password?'}
            </CardTitle>
            <CardDescription>
              {sent
                ? 'If an account exists with that email, we sent a reset link.'
                : "Enter your email and we'll send you a reset link."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4" data-testid="forgot-password-success">
                <div className="flex justify-center">
                  <div className="h-16 w-16 rounded-full bg-[#5AA2AE]/10 flex items-center justify-center">
                    <Mail className="h-8 w-8 text-[#5AA2AE]" />
                  </div>
                </div>
                <p className="text-sm text-center text-slate-600">
                  Didn't receive an email? Check your spam folder or{' '}
                  <button
                    className="text-[#5AA2AE] hover:underline font-medium"
                    onClick={() => setSent(false)}
                  >
                    try again
                  </button>.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="w-full mt-2" data-testid="back-to-login-btn">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
                <div className="text-center">
                  <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700">
                    <ArrowLeft className="h-3 w-3 inline mr-1" />
                    Back to login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
